import AddIcon from "@mui/icons-material/Add";
import { Button, Stack, Typography } from "@mui/material";
import {
  IntegrationDetail,
  ReadinessIssue,
  StepCatalogEntry,
  useGetStepCatalogQuery,
} from "features/integrationsBeta/integrationsBeta";
import { usePlatformEnumLabels } from "features/integrationsBeta/platformEnums";
import { usePlatformRole } from "features/integrationsBeta/usePlatformRole";
import { useMemo, useState } from "react";
import AddStepDialog from "./AddStepDialog";
import SectionCard from "./SectionCard";
import StepConfigCard from "./StepConfigCard";

export default function StepsSection({
  integration,
  issues = [],
  onNavigateToConnection,
}: {
  integration: IntegrationDetail;
  issues?: ReadinessIssue[];
  onNavigateToConnection?: (connectionId: number) => void;
}) {
  const { stageLabel } = usePlatformEnumLabels();
  const { canManageStructure } = usePlatformRole();
  const { data: catalog = [] } = useGetStepCatalogQuery();
  const [addOpen, setAddOpen] = useState(false);

  const entryByKey = useMemo(() => {
    const map: Record<string, StepCatalogEntry> = {};
    for (const entry of catalog) map[entry.step_key] = entry;
    return map;
  }, [catalog]);

  const steps = useMemo(
    () => [...integration.steps].sort((a, b) => a.stage_order - b.stage_order),
    [integration.steps],
  );

  return (
    <SectionCard
      title="Pipeline Steps"
      action={
        canManageStructure ? (
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setAddOpen(true)}
          >
            Add step
          </Button>
        ) : undefined
      }
    >
      <Stack spacing={1.5}>
        {steps.length ? (
          steps.map((step) => (
            <StepConfigCard
              key={step.id}
              step={step}
              integrationId={integration.id}
              catalogEntry={entryByKey[step.step_key] ?? null}
              stageLabel={stageLabel(step.stage)}
              issues={issues.filter((issue) => issue.step_id === step.id)}
              onNavigateToConnection={onNavigateToConnection}
            />
          ))
        ) : (
          <Typography variant="body2" color="text.secondary">
            No steps defined.
          </Typography>
        )}
      </Stack>

      {addOpen && (
        <AddStepDialog
          open
          onClose={() => setAddOpen(false)}
          integration={integration}
        />
      )}
    </SectionCard>
  );
}
